package com.example.positionahead.model

data class LotSnapshot(
    val lot: PositionLot,
    val marketValue: Double,
    val costBasis: Double,
    val unrealizedGainLoss: Double,
    val unrealizedGainLossPercent: Double,
)

data class PositionSnapshot(
    val position: Position,
    val quote: Quote?,
    val marketValue: Double,
    val costBasis: Double,
    val unrealizedGainLoss: Double,
    val unrealizedGainLossPercent: Double,
    val estimatedAnnualIncome: Double,
    val portfolioWeightPercent: Double,
    val lotSnapshots: List<LotSnapshot> = emptyList(),
    val hasLivePrice: Boolean = false,
    val return1MonthPercent: Double? = null,
    val returnYtdPercent: Double? = null,
)

data class PortfolioSummary(
    val totalMarketValue: Double,
    val totalCostBasis: Double,
    val totalUnrealizedGainLoss: Double,
    val totalUnrealizedGainLossPercent: Double,
    val estimatedAnnualIncome: Double,
    val stockAllocationPercent: Double,
    val bondAllocationPercent: Double,
    val cashAllocationPercent: Double,
    val return1MonthPercent: Double? = null,
    val returnYtdPercent: Double? = null,
    val positions: List<PositionSnapshot>,
)

enum class RecommendationAction {
    BUY,
    SELL,
    HOLD,
    REBALANCE,
}

data class Recommendation(
    val action: RecommendationAction,
    val title: String,
    val detail: String,
    val priority: Int,
    val sellSymbol: String? = null,
    val sellAmountDollars: Double? = null,
    val buySymbol: String? = null,
    val buyAmountDollars: Double? = null,
) {
    fun tradeSummary(): String? {
        val sell = sellSymbol
        val buy = buySymbol
        val sellAmt = sellAmountDollars
        val buyAmt = buyAmountDollars
        fun dollars(amount: Double) = kotlin.math.round(amount).toInt()
        return when {
            sell != null && buy != null && sellAmt != null && buyAmt != null ->
                "Trade: Sell $${dollars(sellAmt)} of $sell → Buy $${dollars(buyAmt)} of $buy"
            sell != null && sellAmt != null -> "Trade: Sell $${dollars(sellAmt)} of $sell"
            buy != null && buyAmt != null -> "Trade: Buy $${dollars(buyAmt)} of $buy"
            else -> null
        }
    }
}

data class RebalanceTrade(
    val sellSymbol: String?,
    val sellAmountDollars: Double,
    val buySymbol: String?,
    val buyAmountDollars: Double,
    val reason: String,
)

data class TargetHolding(
    val symbol: String,
    val targetWeightPercent: Double,
    val targetMarketValue: Double,
)

data class PortfolioProjectionPoint(
    val months: Int,
    val currentPortfolioValue: Double,
    val suggestedPortfolioValue: Double,
    val deltaValue: Double,
    /** Est. annual dividend income at projected portfolio value (market yield × value). */
    val currentEstimatedAnnualIncome: Double = 0.0,
    val suggestedEstimatedAnnualIncome: Double = 0.0,
    val currentBlendedYieldPercent: Double = 0.0,
    val suggestedBlendedYieldPercent: Double = 0.0,
)
